import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import type { LinkReply } from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useReadOnly } from '@/auth/useViewingAs';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton } from '@/components/ui/Skeletons';
import { useToast } from '@/components/ui/useToast';
import { useArcDraft, useArcOrganizationId } from '@/lib/arcDraft';
import { STATUS_LABELS, isWritable } from '@/lib/organization';

import { ClicksLine } from './ClicksLine';
import type { LinkCopy } from './linkCopy';
import { canPublish, canSave, linkFormSchema, type LinkForm } from './linkSchema';
import {
  offers,
  statusWord,
  toLinkView,
  type LinkKind,
  type LinkView,
} from './linkState';
import { LinkStatusChip } from './LinkStatusChip';
import { LinkUrlCard } from './LinkUrlCard';
import { ToggleLine } from './ToggleLine';
import { readFailure, useLinks } from './useLinks';
import { WRITE_FALLBACK, classifyLinkError, useLinkWrites } from './useLinkWrites';

/** Which of the ARC's header buttons each link stands behind. */
const HEADER_BUTTON = { fundraising: 'donate', volunteer: 'volunteer' } as const;

/** What a publish says once it lands (P17). */
const PUBLISHED_TOAST = {
  fundraising: 'Fundraising link published.',
  volunteer: 'Volunteer link published.',
} as const;

type LinkFormInput = z.input<typeof linkFormSchema>;

/** The form as the saved draft fills it; the volunteer link is only ever EXTERNAL (P9). */
function formValues(kind: LinkKind, url: string | null | undefined): LinkFormInput {
  return { url: url ?? '', mode: kind === 'volunteer' ? 'EXTERNAL' : undefined };
}

interface LinkPageProps {
  kind: LinkKind;
  copy: LinkCopy;
  /** Above the URL card: Volunteer Recruitment's method choice, and its refusal. */
  modeSelector?: (view: LinkView, writable: boolean, error: string | null) => ReactNode;
  /** Under everything: Fundraising's disclosure panels. */
  disclosures?: ReactNode;
}

/**
 * One page for either link behind the ARC's header buttons: its state, its
 * address, how often the button was tapped, and whether the button is on in
 * the ARC Builder (PB1).
 *
 * Every organization role may open it. Admins and Super Admins may change
 * the link; staff viewing the organization, and anybody in a suspended or
 * deactivated one, see the same page read-only and are told why (P20).
 *
 * The address is a form checked by the same rules as the backend before
 * anything is sent (P13, P14). Save Draft sends it; Publish Changes makes the
 * saved draft live, saving unsaved edits first (P18); Unpublish and Remove
 * link ask before they act (P4). A refusal is shown where the person acts and
 * what they typed is kept (P17); each new state is announced (P23). A
 * refetch, on window focus for instance, never overwrites what is being
 * typed (P25).
 *
 * Its test IDs are written `arc-${kind}-…`, so the areas are
 * `arc-fundraising` and `arc-volunteer` (docs/test-ids.md, P32).
 *
 * Switching organization changes no route, so the page is mounted afresh
 * for each organization: nothing typed, refused or asked in one is carried
 * into the next (P40).
 */
export function LinkPage(props: LinkPageProps) {
  const organizationId = useArcOrganizationId();
  return <LinkPageFor key={organizationId ?? 'none'} {...props} />;
}

function LinkPageFor({ kind, copy, modeSelector, disclosures }: LinkPageProps) {
  const { user } = useAuth();
  const viewing = useReadOnly();
  const ownStatus = user?.organizations.find((o) => o.id === user.organizationId)?.status;
  const writable = !viewing && isWritable(ownStatus);
  const toast = useToast();

  const links = useLinks();
  const arc = useArcDraft();
  const view = links.data?.[kind];

  // The form follows the saved draft, keeping whatever is being typed (P25).
  // Keyed on the address alone: `view` is a new object on every read.
  const loaded = view !== undefined;
  const savedUrl = view?.url;
  const values = useMemo(
    () => (loaded ? formValues(kind, savedUrl) : undefined),
    [kind, loaded, savedUrl],
  );
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<LinkFormInput, unknown, LinkForm>({
    resolver: zodResolver(linkFormSchema),
    mode: 'onTouched',
    values,
    resetOptions: { keepDirtyValues: true },
    defaultValues: formValues(kind, null),
  });
  const typed = useWatch({ control, name: 'url' });

  // Clean against what was written: the saved, trimmed address replaces what
  // was typed. The form's `keepDirtyValues` is for refetches, not for this.
  const writes = useLinkWrites(kind, (link) =>
    reset(formValues(kind, link.url), { keepDirtyValues: false }),
  );
  // A refused write's sentence above the card (P17).
  const [refusal, setRefusal] = useState<string | null>(null);
  // A 403 that got through: the write controls stay off (PB13, P17).
  const [forbidden, setForbidden] = useState(false);
  // A refusal of the volunteer method, shown on the selector (P9).
  const [modeError, setModeError] = useState<string | null>(null);
  const editable = writable && !forbidden;
  // Leaving with an edit asks, a 403 included: the edit is kept, so it is
  // still unsaved (P19, P17).
  const guard = useUnsavedChanges(isDirty && writable);

  /** Puts a refused write where the person acts; the input is always kept (P17, NF4). */
  const placeRefusal = (fallback: string) => (error: unknown) => {
    const placed = classifyLinkError(error, fallback);
    switch (placed.kind) {
      case 'field':
        setError('url', { type: 'server', message: placed.message });
        setFocus('url');
        return;
      case 'mode':
        setModeError(placed.message);
        return;
      case 'forbidden':
        setRefusal(placed.message);
        setForbidden(true);
        return;
      case 'conflict':
        // "Add a link before publishing", above the actions (P17, B7).
        setRefusal(placed.message);
        return;
      case 'elsewhere':
        // A 401, said once by the session handler (P34, NF3).
        return;
      default:
        toast.show({ message: placed.message, severity: 'error' });
    }
  };

  // The chip's new word after a write, for screen readers (P23, PB20).
  const [announcement, setAnnouncement] = useState('');
  const announce = (link: LinkReply) =>
    setAnnouncement(
      `${copy.cardTitle}: ${statusWord(toLinkView(link, view?.range ?? 0))}`,
    );

  // Unpublish and Remove link ask first (P4).
  const [confirming, setConfirming] = useState<'unpublish' | 'remove' | null>(null);
  // A confirmed one takes its own button away, so focus goes to the field (P23).
  const focusFieldOnClose = useRef(false);
  useEffect(() => {
    if (confirming === null && focusFieldOnClose.current) {
      focusFieldOnClose.current = false;
      setFocus('url');
    }
  }, [confirming, setFocus]);

  /** A new write clears the last one's refusals. */
  const startWrite = () => {
    setRefusal(null);
    setModeError(null);
  };

  const save = handleSubmit(({ url }) => {
    startWrite();
    writes.save.mutate(
      { url },
      {
        onSuccess: (link) => {
          toast.show({ message: 'Draft saved.' });
          announce(link);
        },
        onError: placeRefusal(WRITE_FALLBACK.save),
      },
    );
  });

  const published = {
    onSuccess: (link: LinkReply) => {
      toast.show({ message: PUBLISHED_TOAST[kind] });
      announce(link);
    },
    onError: placeRefusal(WRITE_FALLBACK.publish),
  };

  // Unsaved edits are saved first, then published, as one action (P18, B4, B7).
  const publishEdits = handleSubmit(({ url }) => {
    startWrite();
    writes.saveAndPublish.mutate({ url }, published);
  });

  const publish = () => {
    if (isDirty) {
      void publishEdits();
      return;
    }
    startWrite();
    writes.publish.mutate(undefined, published);
  };

  const confirmed = (message: string, fallback: string) => ({
    onSuccess: (link: LinkReply) => {
      toast.show({ message });
      announce(link);
      focusFieldOnClose.current = true;
    },
    onError: placeRefusal(fallback),
    onSettled: () => setConfirming(null),
  });

  const confirm = () => {
    startWrite();
    if (confirming === 'unpublish') {
      writes.unpublish.mutate(
        undefined,
        confirmed('Link unpublished.', WRITE_FALLBACK.unpublish),
      );
    } else {
      writes.remove.mutate(undefined, confirmed('Link removed.', WRITE_FALLBACK.remove));
    }
  };

  const offered = view ? offers(view, writable) : null;

  const header = (
    <PageHeader
      title={copy.title}
      description={copy.subtitle}
      titleAdornment={view ? <LinkStatusChip view={view} /> : null}
      action={
        offered?.save ? (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={() => void save()}
              disabled={!canSave(typed) || writes.pending || !editable}
              data-testid={`arc-${kind}-save-button`}
            >
              Save Draft
            </Button>
            {offered.publish ? (
              <Button
                onClick={publish}
                disabled={
                  !canPublish(typed) ||
                  writes.pending ||
                  !editable ||
                  // A Published link with nothing new: never send the no-op (B10, P18).
                  (offered.nothingToPublish && !isDirty)
                }
                data-testid={`arc-${kind}-publish-button`}
              >
                Publish Changes
              </Button>
            ) : null}
            {offered.unpublish ? (
              <Button
                variant="secondary"
                onClick={() => setConfirming('unpublish')}
                disabled={writes.pending || !editable}
                data-testid={`arc-${kind}-unpublish-button`}
              >
                Unpublish
              </Button>
            ) : null}
          </Box>
        ) : null
      }
    />
  );

  // Only a read that never loaded gives way to the error; a failed refresh
  // keeps the form and what is typed in it (P25, P40).
  if (links.isError && !links.data) {
    const failure = readFailure(links.error);
    return (
      <Box data-testid={`arc-${kind}-page`}>
        {header}
        {/* A 401 is said once, by the session handler; the page draws
            nothing of its own for it (NF3). */}
        {failure === 'retry' ? (
          <ErrorState
            title="The link could not be loaded."
            onRetry={() => void links.refetch()}
            testId={`arc-${kind}`}
          />
        ) : null}
        {/* Nothing to retry: the same request would give the same answer. */}
        {failure === 'not-found' ? (
          <ErrorState
            title="This organization was not found. It may have been removed."
            testId={`arc-${kind}`}
          />
        ) : null}
      </Box>
    );
  }

  const enabled = arc.data?.header[HEADER_BUTTON[kind]].enabled;

  return (
    <Box data-testid={`arc-${kind}-page`}>
      {header}

      {!writable ? (
        <Alert
          severity="info"
          sx={{ mb: 3, maxWidth: 760 }}
          data-testid={`arc-${kind}-read-only-alert`}
        >
          {viewing
            ? 'You are viewing this organization as Activist staff, so its links can be tested but not changed.'
            : `This organization is ${(STATUS_LABELS[ownStatus ?? ''] ?? 'not active').toLowerCase()}, so its links cannot be changed. Contact Activist Technologies to discuss it.`}
        </Alert>
      ) : null}

      {links.isError && readFailure(links.error) !== 'elsewhere' ? (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void links.refetch()}
              data-testid={`arc-${kind}-refresh-retry-button`}
            >
              Try again
            </Button>
          }
          sx={{ mb: 3, maxWidth: 760 }}
          data-testid={`arc-${kind}-refresh-error-alert`}
        >
          The link could not be refreshed, so what is shown may be out of date.
        </Alert>
      ) : null}

      {refusal ? (
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 760 }}
          data-testid={`arc-${kind}-write-error`}
        >
          {refusal}
        </Alert>
      ) : null}

      {!view ? (
        <Box data-testid={`arc-${kind}-loading`}>
          <CardSkeleton
            lines={4}
            label="Loading the link"
            minHeight={{ xs: 236, sm: 196 }}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'minmax(0, 1fr)' }}>
          {modeSelector?.(view, editable, modeError)}
          <LinkUrlCard
            view={view}
            copy={copy}
            writable={editable}
            field={register('url')}
            value={typed}
            error={errors.url?.message}
            onRemove={offered?.remove ? () => setConfirming('remove') : undefined}
            removeDisabled={writes.pending || !editable}
          />
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <ClicksLine view={view} />
            <ToggleLine kind={kind} button={copy.button} enabled={enabled} />
          </Box>
          {disclosures}
        </Box>
      )}

      <ConfirmDialog
        open={confirming === 'unpublish'}
        title={`Unpublish the ${kind} link?`}
        description={`The ${copy.button} button leaves the app until the link is published again. Its address is kept as a draft.`}
        confirmLabel="Unpublish"
        pending={writes.pending}
        onConfirm={confirm}
        onCancel={() => setConfirming(null)}
        testId={`arc-${kind}-unpublish-dialog`}
      />
      <ConfirmDialog
        open={confirming === 'remove'}
        title={`Remove the ${kind} link?`}
        description="Its draft address is deleted and the link goes back to Not set."
        confirmLabel="Remove link"
        destructive
        pending={writes.pending}
        onConfirm={confirm}
        onCancel={() => setConfirming(null)}
        testId={`arc-${kind}-remove-dialog`}
      />
      <LiveRegion message={announcement} />
      <UnsavedChangesDialog {...guard} />
    </Box>
  );
}
